import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  Dispatch,
} from 'react';
import _ from 'lodash';
import { Category } from '@wix/ambassador-marketplace-categories-v1-category/types';
import { GeoData } from '@wix/wix-common-aspects';
import {
  ClientProject,
  Language,
  UserDetails,
  LocaleDataTranslations,
  PartnerProfileEnriched,
  ProfilePageType,
} from '@common/types';
import { DEFAULT_CURRENCY } from '@utils/constants';
import usePortfolioUrlPreloading from '@hooks/usePortfolioUrlPreloader';
import { PublicCertification } from '@wix/ambassador-academy-certifications-v1-account-certification/types';

interface ProfileContextProviderProps {
  children?: ReactNode;
  partnerProfile: PartnerProfileEnriched;
  languages?: Array<Language>;
  language: string;
  staticLocaleUrl: string;
  services?: Array<Category>;
  geo?: GeoData;
  userDetails?: UserDetails;
  origin?: string;
  host: string;
  entry?: string;
  isMyProfile?: boolean;
  enabled?: boolean;
  error?: string;
  isRenderedInModal?: boolean;
  isMobileUserAgent?: boolean;
  userCurrency?: string;
  currencyConversionRate?: number;
  shouldDisplayTraceabilityData?: boolean;
  profilePageType?: ProfilePageType;
  accountCertifications?: PublicCertification[];
}

type State = {
  appStatus: string;
  partnerProfile: PartnerProfileEnriched;
  languages?: Array<Language>;
  language: string;
  staticLocaleUrl: string;
  services?: Array<Category>;
  offeredServices?: Array<any>;
  geo?: GeoData;
  userCurrency: string;
  currencyConversionRate: number;
  localeDataTranslations: LocaleDataTranslations;
  userDetails?: UserDetails;
  origin?: string;
  host: string;
  entry?: string;
  isMyProfile?: boolean;
  enabled: boolean;
  isRenderedInModal?: boolean;
  isMobileUserAgent?: boolean;
  shouldDisplayTraceabilityData?: boolean;
  profilePageType?: ProfilePageType;
  accountCertifications?: PublicCertification[];
  portfolioUrlMappings: Record<string, string | null>;
  portfolioUrlsLoading: boolean;
  copyStatus: null | 'success' | 'error';
};

interface ProfileContextType extends State {
  dispatch: Dispatch<ActionType>;
}

const ProfileContext = createContext<ProfileContextType | null>(null);

function mainReducer(state: State, action: ActionType) {
  switch (action.type) {
    case ACTIONS.SET_OFFERED_SERVICES: {
      const { offeredServices } = action;
      return {
        ...state,
        offeredServices,
      };
    }
    case ACTIONS.SET_PROJECTS: {
      const { projects, projectsCursor } = action;
      const _partnerProfile = _.cloneDeep(state.partnerProfile);
      _.set(_partnerProfile, 'projects', [
        ..._.get(state.partnerProfile, 'projects'),
        ...projects,
      ]);
      _.set(_partnerProfile, 'projectsCursor', projectsCursor);
      return {
        ...state,
        partnerProfile: _partnerProfile,
      };
    }
    case ACTIONS.RESET_PROJECTS: {
      const { projects } = action;
      const _partnerProfile = _.cloneDeep(state.partnerProfile);
      _.set(_partnerProfile, 'projects', [...projects]);
      return {
        ...state,
        partnerProfile: _partnerProfile,
      };
    }
    case ACTIONS.UPDATE_PROJECT: {
      const { project } = action;
      if (!project) {
        return state;
      }
      const _partnerProfile = _.cloneDeep(state.partnerProfile);
      const projects = _.get(state.partnerProfile, 'projects');
      const projectIndex = projects.findIndex(
        (p: ClientProject) => p.id === project.id,
      );
      projects[projectIndex] = project;
      _.set(_partnerProfile, 'projects', projects);
      return {
        ...state,
        partnerProfile: _partnerProfile,
      };
    }
    case ACTIONS.UPDATE_PORTFOLIO_URL: {
      const { metaSiteId, url } = action;
      return {
        ...state,
        portfolioUrlMappings: {
          ...state.portfolioUrlMappings,
          [metaSiteId]: url,
        },
      };
    }
    case ACTIONS.SET_PORTFOLIO_LOADING: {
      const { isLoading } = action;
      return {
        ...state,
        portfolioUrlsLoading: isLoading,
      };
    }
    case ACTIONS.SET_COPY_STATUS: {
      const { copyStatus } = action;
      return {
        ...state,
        copyStatus,
      };
    }
    default:
      throw new Error();
  }
}

export const ACTIONS = {
  SET_PROJECTS: 'SET_PROJECTS',
  RESET_PROJECTS: 'RESET_PROJECTS',
  SET_OFFERED_SERVICES: 'SET_OFFERED_SERVICES',
  UPDATE_PROJECT: 'UPDATE_PROJECT',
  UPDATE_PORTFOLIO_URL: 'UPDATE_PORTFOLIO_URL',
  SET_PORTFOLIO_LOADING: 'SET_PORTFOLIO_LOADING',
  SET_COPY_STATUS: 'SET_COPY_STATUS',
} as const;

type ActionType =
  | {
      type: typeof ACTIONS.SET_PROJECTS;
      projects: ClientProject[];
      projectsCursor?: string;
    }
  | { type: typeof ACTIONS.RESET_PROJECTS; projects: ClientProject[] }
  | { type: typeof ACTIONS.SET_OFFERED_SERVICES; offeredServices: Category[] }
  | { type: typeof ACTIONS.UPDATE_PROJECT; project: ClientProject | undefined }
  | {
      type: typeof ACTIONS.UPDATE_PORTFOLIO_URL;
      metaSiteId: string;
      url: string | null;
    }
  | { type: typeof ACTIONS.SET_PORTFOLIO_LOADING; isLoading: boolean }
  | {
      type: typeof ACTIONS.SET_COPY_STATUS;
      copyStatus: null | 'success' | 'error';
    };

export function ProfileContextProvider(props: ProfileContextProviderProps) {
  const offeredServices = useMemo(() => {
    const offeredServiceIds = new Map(
      props.partnerProfile?.services?.values
        ?.filter((service) => service.id)
        .map((service) => [service.id!, service.minPrice!]) ?? [],
    );

    return (props.services || [])
      .map((category) => {
        const offeredServices = (category.services || [])
          .filter((service) => service.id && offeredServiceIds.has(service.id))
          .map((service) => ({
            ...service,
            minPrice: offeredServiceIds.get(service.id!),
          }));

        return offeredServices.length > 0
          ? { ...category, services: offeredServices }
          : null;
      })
      .filter((category) => category !== null) as Category[];
  }, [props.partnerProfile, props.services]);

  const [state, dispatch] = useReducer(mainReducer, {
    appStatus: 'initialized',
    partnerProfile: props.partnerProfile,
    geo: props.geo,
    languages: props.languages,
    language: props.language || 'en',
    staticLocaleUrl: props.staticLocaleUrl,
    services: props.services,
    userDetails: props.userDetails,
    offeredServices: offeredServices,
    origin: props.origin,
    host: props.host,
    entry: props.entry,
    isMyProfile: props.isMyProfile,
    enabled: Boolean(props.enabled),
    isRenderedInModal: props.isRenderedInModal,
    userCurrency: props?.userCurrency ?? DEFAULT_CURRENCY,
    currencyConversionRate: props?.currencyConversionRate ?? 1,
    isMobileUserAgent: props.isMobileUserAgent,
    localeDataTranslations: {},
    shouldDisplayTraceabilityData: !!props.shouldDisplayTraceabilityData,
    profilePageType: props.profilePageType,
    accountCertifications: props.accountCertifications || [],
    portfolioUrlMappings: {},
    portfolioUrlsLoading: false,
    copyStatus: null,
  });

  const isProd = process.env.NODE_ENV === 'production';

  const baseAPI = isProd
    ? `${props.host}/partners-marketplace`
    : `${props.host}`;

  const metaSiteIds = useMemo(() => {
    return props.partnerProfile.portfolio?.items
      ? props.partnerProfile.portfolio.items
          .filter((item) => item.metaSiteId)
          .map((item) => item.metaSiteId!)
          .filter((id, index, array) => array.indexOf(id) === index)
      : [];
  }, []);

  usePortfolioUrlPreloading(
    baseAPI,
    metaSiteIds,
    dispatch,
    state.portfolioUrlMappings,
  );

  useEffect(() => {
    dispatch({
      type: ACTIONS.SET_OFFERED_SERVICES,
      offeredServices: offeredServices,
    });
  }, [offeredServices]);

  return (
    <ProfileContext.Provider
      value={{
        ...state,
        dispatch,
      }}
    >
      {props.children}
    </ProfileContext.Provider>
  );
}

export function useProfileContext() {
  const appContext = useContext(ProfileContext);
  if (!appContext) {
    throw new Error(
      'useProfileContext must be used within the ProfileContext.Provider',
    );
  }
  return appContext;
}
